import { useEffect, useState } from "react";
import { User, Mail, Phone, LogOut, ShieldCheck, KeyRound, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import MemberLayout, { useMember } from "@/components/app/MemberLayout";
import memberApi from "@/lib/memberApi";

/**
 * Account page — profile info, personal magic-link, and logout.
 *
 * IMPORTANT: `useMember()` reads a Context that is provided by <MemberLayout />
 * itself. If we called `useMember()` at the top of this file's default export
 * (a SIBLING of MemberLayout in the JSX tree), the hook would return `null`.
 * So we split into an outer wrapper (renders MemberLayout) and an inner
 * `<AccountContent />` component that lives inside the Provider.
 */
export default function Account() {
  return (
    <MemberLayout>
      <AccountContent />
    </MemberLayout>
  );
}

function AccountContent() {
  const { me, logout } = useMember() || {};
  const [magicLinkUrl, setMagicLinkUrl] = useState(null);
  const [loadingLink, setLoadingLink] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await memberApi.get("/community/magic-link");
        setMagicLinkUrl(data.magic_link_url);
      } catch (_) {
        /* silent — the section will just show a friendly fallback */
      } finally {
        setLoadingLink(false);
      }
    })();
  }, []);

  const handleCopy = async () => {
    if (!magicLinkUrl) return;
    try {
      await navigator.clipboard.writeText(magicLinkUrl);
      setCopied(true);
      toast.success("הלינק הועתק!");
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      toast.error("לא הצלחנו להעתיק — סמן ידנית.");
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-8 py-8" data-testid="account-page">
      <div className="mb-8">
        <div className="text-xs font-bold text-amber-500 tracking-widest uppercase mb-2">חשבון</div>
        <h1 className="font-[Rubik] font-black text-3xl sm:text-4xl text-stone-50">החשבון שלי</h1>
      </div>

      <div className="bg-stone-900 border border-stone-800 rounded-2xl p-6 space-y-4">
        <Row icon={User} label="שם">{me?.name || "—"}</Row>
        <Row icon={Mail} label="אימייל">{me?.email || "—"}</Row>
        <Row icon={Phone} label="טלפון">{me?.phone || "—"}</Row>
        <Row icon={ShieldCheck} label="סטטוס חבר/ה">
          <span className="inline-flex items-center gap-1 text-emerald-400 font-bold">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
            פעיל/ה
          </span>
        </Row>
      </div>

      {/* Magic Link section — the user's personal login URL */}
      <div className="mt-6 bg-stone-900 border border-stone-800 rounded-2xl p-6" data-testid="account-magic-link-section">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-lg bg-amber-950/50 border border-amber-800/40 flex items-center justify-center">
            <KeyRound className="w-4 h-4 text-amber-500" />
          </div>
          <div>
            <div className="font-bold text-stone-100">הלינק האישי שלך לכניסה</div>
            <div className="text-xs text-stone-500 mt-0.5">שמרו אותו בסימנייה או שלחו לעצמכם</div>
          </div>
        </div>

        <p className="text-sm text-stone-400 leading-relaxed mb-4">
          כל פעם שתרצו להיכנס מחדש (או ממכשיר אחר), פשוט לחצו על הלינק הזה. אין צורך בסיסמה או בקוד — הלחיצה עצמה מזהה אתכם ומכניסה אתכם ישר לקהילה.
        </p>

        {loadingLink ? (
          <div className="h-11 bg-stone-950/60 rounded-lg animate-pulse" />
        ) : magicLinkUrl ? (
          <div className="flex flex-col sm:flex-row gap-2 items-stretch">
            <input
              type="text"
              readOnly
              value={magicLinkUrl}
              dir="ltr"
              className="flex-1 min-w-0 bg-stone-950 border border-stone-800 rounded-lg px-3 py-2.5 text-xs sm:text-sm text-stone-300 font-mono focus:outline-none focus:border-amber-600 focus:ring-1 focus:ring-amber-600"
              data-testid="account-magic-link-input"
              onFocus={(e) => e.currentTarget.select()}
            />
            <button
              type="button"
              onClick={handleCopy}
              data-testid="account-magic-link-copy"
              className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-bold transition-colors flex-shrink-0 ${
                copied
                  ? "bg-emerald-600 text-white"
                  : "bg-amber-600 hover:bg-amber-500 text-stone-950"
              }`}
            >
              {copied ? (<><Check className="w-4 h-4" /> הועתק</>) : (<><Copy className="w-4 h-4" /> העתקת הלינק</>)}
            </button>
          </div>
        ) : (
          <p className="text-sm text-stone-500 leading-relaxed">
            לא הצלחנו להביא את הלינק כרגע. רעננו את הדף — בדרך כלל זה עוזר.
          </p>
        )}

        <div className="mt-4 bg-amber-950/20 border border-amber-800/30 rounded-lg px-4 py-3 text-xs text-amber-200/90 leading-relaxed">
          <strong className="text-amber-400">חשוב:</strong> הלינק אישי — אל תשתפו אותו עם אחרים. מי שיש לו את הלינק שלכם — יכול להיכנס לחשבון שלכם.
        </div>
      </div>

      <button
        onClick={logout}
        data-testid="account-logout"
        className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-stone-900 border border-red-800/50 hover:bg-red-950/50 text-red-400 font-bold px-6 py-4 rounded-xl transition-colors"
      >
        <LogOut className="w-5 h-5" />
        התנתקות מהחשבון
      </button>

      <p className="text-xs text-stone-500 text-center mt-6 leading-relaxed">
        לפרטים נוספים, שינוי פרטים או ביטול חשבון — פנו אלינו דרך דף יצירת הקשר באתר.
      </p>
    </div>
  );
}

function Row({ icon: Icon, label, children }) {
  return (
    <div className="flex items-center gap-4 py-2 border-b border-stone-800 last:border-b-0">
      <div className="w-9 h-9 rounded-lg bg-stone-800 flex items-center justify-center">
        <Icon className="w-4 h-4 text-amber-500" />
      </div>
      <div className="flex-1">
        <div className="text-xs font-bold text-stone-500 uppercase tracking-wider">{label}</div>
        <div className="text-stone-100">{children}</div>
      </div>
    </div>
  );
}
